/**
 * One claim, as the desk handles it.
 *
 * The three actions are three permissions, and the split is by consequence
 * rather than by table: taking a claim and answering it are the desk's daily
 * work, and *deciding* it is the distributor's answer to the customer. At a
 * distributor of any size those are not the same person, which is why the
 * decision's button is absent — not disabled — for a role without it.
 *
 * The refusal dialog demands its reason. The server refuses one without and
 * the database refuses it too, and asking here as well is not redundancy: it
 * is the difference between a validation message and a failed request.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { download, useAction, useResource } from "@hardauto/core-data";
import { day, moment, EM_DASH } from "@hardauto/core-i18n";
import { Field } from "@hardauto/core-forms";
import {
  Alert,
  Badge,
  Button,
  Card,
  DefinitionList,
  Dialog,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  Textarea,
  useToast,
  type Definition,
} from "@hardauto/core-ui";

import { SupportApi, type TicketStatus } from "../api";
import { NotesPanel } from "../NotesPanel";
import { Thread } from "../Thread";
import { Timeline } from "../Timeline";
import { NAMESPACE } from "../i18n";
import { SUPPORT_PATHS } from "../paths";
import { STATUS_TONE } from "../status";
import "./support.css";

/** Which decision the dialog is asking about. */
type Decision = Extract<TicketStatus, "resolved" | "refused" | "in_progress">;

export function TicketScreen({ client, id }: { client: ApiClient; id: string }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new SupportApi(client), [client]);
  const action = useAction();
  const toast = useToast();

  const mayWrite = useCan("support.ticket.write");
  const mayDecide = useCan("support.ticket.decide");
  const mayReadNotes = useCan("support.note.read");

  const [deciding, setDeciding] = useState<Decision | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fetcher = useCallback(() => api.get(id), [api, id]);
  const ticket = useResource(fetcher);

  if (ticket.loading) return <LoadingState label={t("ticket.loading")} />;
  if (ticket.failed && !ticket.data) {
    return (
      <ErrorState
        title={t("error.title")}
        description={t("error.body")}
        onRetry={ticket.reload}
        retryLabel={t("error.retry")}
      />
    );
  }
  if (!ticket.data) {
    return (
      <EmptyState title={t("ticket.notFound.title")} description={t("ticket.notFound.body")} />
    );
  }

  const detail = ticket.data;
  const subject = detail.ticket;

  const meta: Definition[] = [
    {
      label: t("ticket.meta.client"),
      value: `${detail.client_name} (${detail.client_code})`,
    },
    { label: t("ticket.meta.reason"), value: t(`reason.${subject.reason}`) },
    { label: t("ticket.meta.order"), value: subject.order_reference || EM_DASH },
    { label: t("ticket.meta.article"), value: subject.article_reference || EM_DASH },
    {
      label: t("ticket.meta.quantity"),
      value: subject.quantity > 0 ? String(subject.quantity) : EM_DASH,
    },
    { label: t("ticket.meta.site"), value: subject.site_code || EM_DASH },
    { label: t("ticket.meta.opened"), value: moment(subject.opened_at, locale) },
    {
      label: t("ticket.meta.decided"),
      value: subject.decided_at ? moment(subject.decided_at, locale) : EM_DASH,
    },
  ];

  function decisionTitle(decision: Decision): string {
    if (decision === "resolved") return t("ticket.decision.resolveTitle");
    if (decision === "refused") return t("ticket.decision.refuseTitle");
    return t("ticket.decision.reopenTitle");
  }

  function decisionBody(decision: Decision): string {
    if (decision === "resolved") return t("ticket.decision.resolveBody");
    if (decision === "refused") return t("ticket.decision.refuseBody");
    return t("ticket.decision.reopenBody");
  }

  return (
    <Page
      title={subject.subject}
      eyebrow={
        <>
          <code className="hardauto-support__reference">{subject.reference}</code> ·{" "}
          <Badge tone={STATUS_TONE[subject.status]}>{t(`status.${subject.status}`)}</Badge>
          {detail.owner_name ? ` · ${detail.owner_name}` : ""}
        </>
      }
      back={<Link to={SUPPORT_PATHS.tickets}>{t("ticket.back")}</Link>}
      actions={
        <>
          {mayWrite && subject.status === "open" ? (
            <Button
              busy={action.isBusy("take")}
              busyLabel={t("ticket.take")}
              onClick={() =>
                void action.run("take", async () => {
                  try {
                    await api.take(subject.id);
                    toast.show(t("ticket.taken"), { tone: "success" });
                    ticket.reload();
                  } catch {
                    toast.show(t("ticket.takeFailed"), { tone: "danger" });
                  }
                })
              }
            >
              {t("ticket.take")}
            </Button>
          ) : null}

          {/* Absent, not disabled, for a role without the permission: a
              control that looks live and is not teaches people to click it. */}
          {mayDecide && subject.status !== "resolved" && subject.status !== "refused" ? (
            <>
              <Button onClick={() => setDeciding("refused")}>{t("ticket.refuse")}</Button>
              <Button variant="primary" onClick={() => setDeciding("resolved")}>
                {t("ticket.resolve")}
              </Button>
            </>
          ) : null}

          {mayDecide && (subject.status === "resolved" || subject.status === "refused") ? (
            <Button onClick={() => setDeciding("in_progress")}>{t("ticket.reopen")}</Button>
          ) : null}
        </>
      }
    >
      <div className="hardauto-stack">
        {subject.decision_reason ? (
          <Alert
            tone={subject.status === "refused" ? "warning" : "success"}
            title={t("ticket.reason")}
          >
            {subject.decision_reason}
          </Alert>
        ) : null}

        <div className="hardauto-support__split">
          <Card title={t("ticket.meta.title")} compactTitle>
            <DefinitionList items={meta} layout="inline" />
          </Card>
          <Card title={t("ticket.timeline")} compactTitle>
            <Timeline steps={subject.timeline} />
          </Card>
        </div>

        <Thread
          messages={subject.messages}
          allowInternal
          {...(mayWrite
            ? {
                onSend: async (body: string, options: { internal: boolean }) => {
                  await api.reply(subject.id, body, { internal: options.internal });
                  ticket.reload();
                },
              }
            : {})}
        />

        <Card
          title={t("ticket.attachments.title")}
          actions={
            mayWrite ? (
              <label className="hardauto-support__upload">
                <span>{t("ticket.attachments.add")}</span>
                <input
                  type="file"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (!file) return;
                    void action.run("attach", async () => {
                      try {
                        await api.attach(subject.id, file);
                        toast.show(t("ticket.attachments.added"), { tone: "success" });
                        ticket.reload();
                      } catch {
                        toast.show(t("ticket.attachments.failed"), { tone: "danger" });
                      }
                    });
                  }}
                />
              </label>
            ) : undefined
          }
        >
          {subject.attachments.length === 0 ? (
            <EmptyState title={t("ticket.attachments.empty")} />
          ) : (
            <ul className="hardauto-support__files">
              {subject.attachments.map((file) => (
                <li key={file.id}>
                  <span>{file.filename}</span>
                  <span className="hardauto-cell__muted">
                    {t(`author.${file.author}`)} · {day(file.uploaded_at, locale)}
                  </span>
                  <Button
                    size="sm"
                    busy={action.isBusy(`file:${file.id}`)}
                    busyLabel={t("ticket.attachments.download")}
                    onClick={() =>
                      void action.run(`file:${file.id}`, async () => {
                        try {
                          download(await api.attachment(subject.id, file.file_id), file.filename);
                        } catch {
                          toast.show(t("ticket.attachments.downloadFailed"), {
                            tone: "danger",
                          });
                        }
                      })
                    }
                  >
                    {t("ticket.attachments.download")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* The customer's own history, beside the claim. The question an ADV
            asks before deciding a warranty is what we already know about
            them, and making them open another screen for it is how a decision
            gets taken without it. */}
        {mayReadNotes ? (
          <NotesPanel client={client} subjectKind="client" subjectId={detail.client_id} />
        ) : null}
      </div>

      <Dialog
        open={deciding !== null}
        onClose={() => setDeciding(null)}
        title={deciding ? decisionTitle(deciding) : ""}
        description={deciding ? decisionBody(deciding) : ""}
        closeLabel={t("ticket.decision.close")}
        footer={
          <>
            <Button onClick={() => setDeciding(null)}>{t("ticket.decision.cancel")}</Button>
            <Button
              variant="primary"
              busy={action.isBusy("decide")}
              busyLabel={t("ticket.decision.submit")}
              onClick={() =>
                void action.run("decide", async () => {
                  if (!deciding) return;
                  setError(null);
                  // Asked here as well as refused there: a validation message
                  // is a better answer than a failed request.
                  if (deciding === "refused" && !reason.trim()) {
                    setError(t("ticket.decision.reasonRequired"));
                    return;
                  }
                  try {
                    await api.decide(subject.id, deciding, reason.trim());
                    toast.show(t("ticket.decision.done"), { tone: "success" });
                    setDeciding(null);
                    setReason("");
                    ticket.reload();
                  } catch {
                    setError(t("ticket.decision.failed"));
                  }
                })
              }
            >
              {t("ticket.decision.submit")}
            </Button>
          </>
        }
      >
        <div className="hardauto-form">
          <Field
            label={t("ticket.decision.reason")}
            required={deciding === "refused"}
            error={error ?? undefined}
          >
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>
    </Page>
  );
}
